export function youtubeUrlToEmbedSrc(raw: string): string | null {
  try {
    const url = new URL(raw.trim());
    const host = url.hostname.replace(/^www\./, '').toLowerCase();

    let videoId = '';
    if (host === 'youtu.be') {
      videoId = url.pathname.replace(/^\//, '');
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname === '/watch') {
        videoId = url.searchParams.get('v') ?? '';
      } else if (url.pathname.startsWith('/embed/')) {
        videoId = url.pathname.split('/embed/')[1] ?? '';
      } else if (url.pathname.startsWith('/shorts/')) {
        videoId = url.pathname.split('/shorts/')[1] ?? '';
      }
    }

    videoId = videoId.split(/[?&#/]/)[0] ?? '';
    if (!videoId) return null;

    const list = url.searchParams.get('list');
    const embed = new URL(`https://www.youtube.com/embed/${encodeURIComponent(videoId)}`);
    if (list) embed.searchParams.set('list', list);
    embed.searchParams.set('enablejsapi', '1');
    return embed.toString();
  } catch {
    return null;
  }
}

export type YoutubeMediaOccurrence = {
  start: number;
  end: number;
  previewSrc: string;
};

function collapseExtraBlankLines(input: string): string {
  return input.replace(/\n{3,}/g, '\n\n').trim();
}

function positionInsideRange(pos: number, ranges: { start: number; end: number }[]): boolean {
  return ranges.some((r) => pos >= r.start && pos < r.end);
}

/**
 * All YouTube media in document order (no deduplication). Wrapped iframe blocks count as one item.
 */
export function findYoutubeMediaOccurrences(html: string): YoutubeMediaOccurrence[] {
  if (!html) return [];

  const occurrences: YoutubeMediaOccurrence[] = [];

  const wrappedIframeRe =
    /<(?:jodit|div)\b[^>]*\bdata-jodit_iframe_wrapper\b[^>]*>[\s\S]*?<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*(?:\/>|>\s*<\/iframe>)[\s\S]*?<\/(?:jodit|div)>/gi;
  let wrappedMatch: RegExpExecArray | null;
  while ((wrappedMatch = wrappedIframeRe.exec(html)) !== null) {
    const previewSrc = (wrappedMatch[1] || '').trim();
    if (!previewSrc) continue;
    occurrences.push({
      start: wrappedMatch.index,
      end: wrappedMatch.index + wrappedMatch[0].length,
      previewSrc,
    });
  }

  const iframeRe = /<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*(?:\/>|>\s*<\/iframe>)/gi;
  let iframeMatch: RegExpExecArray | null;
  while ((iframeMatch = iframeRe.exec(html)) !== null) {
    if (positionInsideRange(iframeMatch.index, occurrences)) continue;
    const previewSrc = (iframeMatch[1] || '').trim();
    if (!previewSrc) continue;
    occurrences.push({
      start: iframeMatch.index,
      end: iframeMatch.index + iframeMatch[0].length,
      previewSrc,
    });
  }

  const ytUrlRegex =
    /\bhttps?:\/\/(?:www\.)?(?:youtu\.be\/[^\s<]+|youtube\.com\/[^\s<]+|m\.youtube\.com\/[^\s<]+)\b/gi;
  let urlMatch: RegExpExecArray | null;
  while ((urlMatch = ytUrlRegex.exec(html)) !== null) {
    if (positionInsideRange(urlMatch.index, occurrences)) continue;
    const raw = urlMatch[0];
    const previewSrc = youtubeUrlToEmbedSrc(raw);
    if (!previewSrc) continue;
    occurrences.push({
      start: urlMatch.index,
      end: urlMatch.index + raw.length,
      previewSrc,
    });
  }

  return occurrences.sort((a, b) => a.start - b.start);
}

/** Preview iframe src list — every occurrence, in document order. */
export function collectYoutubePreviewCandidates(html: string): string[] {
  return findYoutubeMediaOccurrences(html).map((o) => o.previewSrc);
}

export function isYoutubeEmbedSrc(src: string): boolean {
  try {
    const url = new URL(src.trim());
    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    return (
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'youtube-nocookie.com' ||
      host.endsWith('.youtube.com')
    );
  } catch {
    return false;
  }
}

/** Adds params required for Space / postMessage play-pause in preview iframes. */
export function withYoutubeEmbedJsApi(src: string, origin?: string): string {
  if (!src || !isYoutubeEmbedSrc(src)) {
    return src;
  }
  try {
    const url = new URL(src.trim());
    url.searchParams.set('enablejsapi', '1');
    const resolvedOrigin =
      origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
    if (resolvedOrigin) {
      url.searchParams.set('origin', resolvedOrigin);
    }
    return url.toString();
  } catch {
    return src;
  }
}

export function postYoutubeIframeCommand(
  iframe: HTMLIFrameElement,
  command: 'playVideo' | 'pauseVideo',
): void {
  iframe.contentWindow?.postMessage(
    JSON.stringify({ event: 'command', func: command, args: '' }),
    '*',
  );
}

/** Removes the media occurrence at index (same ordering as collectYoutubePreviewCandidates). */
export function removeYoutubeMediaAtIndex(html: string, index: number): string {
  if (!html) return html;
  const occurrences = findYoutubeMediaOccurrences(html);
  const target = occurrences[index];
  if (!target) return html;
  return collapseExtraBlankLines(html.slice(0, target.start) + html.slice(target.end));
}
